"use client";

import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { VariantProps, cva } from "class-variance-authority";
import * as React from "react";

import { cn } from "@punto/ui/lib/utils";

const avatarVariants = cva(
  "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-full",
  {
    variants: {
      // The initials scale with the circle only as far as `md`: Figma sets them
      // in `body` (14px/20) at both 36 and 40, and in `caption` (12px/16) at 24.
      // So `lg` carries `text-sm`, not `text-base` — 16px is not a size the type
      // scale defines at all, and it made the large avatars' initials a step
      // heavier than the design draws them.
      size: {
        sm: "h-6 w-6 text-xs",
        md: "h-8 w-8 text-sm",
        lg: "h-10 w-10 text-sm",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

const avatarFallbackVariants = cva(
  "flex h-full w-full items-center justify-center rounded-full bg-muted text-muted-foreground font-medium",
  {
    variants: {
      variant: {
        default: "",
        me: "bg-success/20 text-success",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

const Avatar = ({
  className,
  size,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Root> &
  VariantProps<typeof avatarVariants>) => (
  <AvatarPrimitive.Root
    className={cn(avatarVariants({ className, size }), className)}
    {...props}
  />
);
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = ({
  className,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Image>) => (
  <AvatarPrimitive.Image
    className={cn("aspect-square h-full w-full", className)}
    {...props}
  />
);
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = ({
  className,
  variant,
  ...props
}: React.ComponentProps<typeof AvatarPrimitive.Fallback> &
  VariantProps<typeof avatarFallbackVariants>) => (
  <AvatarPrimitive.Fallback
    className={cn(avatarFallbackVariants({ className, variant }), className)}
    {...props}
  />
);
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };
